// src/components/hlsPlaylist.ts
/**
 * The HLS manifest both media players fetch, and the one rewrite that makes it playable.
 *
 * WHY THIS IS A SEPARATE MODULE. `VideoPlayer.tsx` and `AudioPlayer.tsx` each carried their own copy
 * of this fetch and of this rewrite, identical apart from the word "video"/"audio" in the log line.
 * The rewrite is the client half of an invariant whose server half is `MediaUrlSigner.signPlaylist`,
 * and an invariant spelled out in two places drifts in one of them. It also could not be tested
 * where it sat: a component that builds a Plyr and an Hls instance on mount is not a unit test.
 * Here it is a string in, a string out, and `hlsPlaylist.test.ts` is the only thing in this lane
 * that can fail in CI rather than in front of a member.
 *
 * THE INVARIANT, both halves. The served manifest keeps every segment line RELATIVE and starting
 * with `output<N>.ts` — since 2026-09 with `?exp=…&sig=…` appended, a short-lived HMAC over
 * (route, id, segment, expiry). This module turns each of those lines into an absolute URL on the
 * API host, because the manifest reaches hls.js as a `blob:` URL and a relative line resolved
 * against a blob URL has no host to resolve against. The signature survives untouched because it
 * sits after the name. An absolute line in the served manifest would be doubled into nonsense here,
 * and a deployed web bundle has no OTA path — which is why the server side of that sentence is
 * pinned by `HlsSignedUrlTest` and written down in `docs/SECURITY.md` §8.
 *
 * WHY THE REWRITE IS ANCHORED TO A WHOLE LINE. The old `/output(\d+).ts/g` ran over the whole body
 * and matched the name wherever it appeared: a tag that names a segment
 * (`#EXT-X-MAP:URI="output0.ts"`) came out corrupted, and a line that was already absolute came out
 * doubled — the exact failure the invariant exists to prevent, reintroduced from the client side.
 * ffmpeg emits neither here today (see `HlsMediaFixture`), so that was latent rather than open; it
 * is anchored now because the cost of being wrong is "every podcast and video plays nothing" on a
 * bundle that cannot be corrected after it ships.
 */
import api from '../api/axiosInstance';

/**
 * A segment line this client must absolutize: the name first, then anything that is neither a slash
 * nor whitespace — which in practice is the signature query and nothing else. A line carrying a
 * slash is already a URL and is left exactly as it is; a line starting with `#` is a tag and cannot
 * match. Deliberately no narrower than the regex it replaces: any line the shipped bundle would
 * have rewritten is still rewritten, so this cannot take away playback that works today.
 */
const RELATIVE_SEGMENT_LINE = /^output\d+\.ts[^/\s]*$/;

/**
 * Rewrites every relative segment line onto `base`, and copies everything else through byte for
 * byte — tags, blank lines, the trailing `#EXT-X-ENDLIST`, and the line terminators themselves.
 *
 * @param base the absolute directory the segments live in, e.g. `https://api.goly-esa.sk/api/posts/42`
 */
export const absolutizeSegmentLines = (playlist: string, base: string): string => {
    const prefix = base.endsWith('/') ? base.slice(0, -1) : base;
    return playlist
        .split('\n')
        .map((rawLine) => {
            // A manifest written with CRLF endings keeps them. The terminator is carried over rather
            // than normalised so the body handed to hls.js differs from the served one in the
            // segment lines and in nothing else.
            const crlf = rawLine.endsWith('\r');
            const line = crlf ? rawLine.slice(0, -1) : rawLine;
            if (!RELATIVE_SEGMENT_LINE.test(line)) {
                return rawLine;
            }
            return `${prefix}/${line}${crlf ? '\r' : ''}`;
        })
        .join('\n');
};

/**
 * The absolute directory a manifest's segments live in: `…/api/posts/42` for
 * `/posts/42/playlist.m3u8`, since the server names them relative to the manifest.
 *
 * Returns null when there is nothing to build one from — no configured `baseURL`, or a path with no
 * directory at all — and the caller then hands the manifest over unrewritten, exactly as both
 * players did before this module existed. Guessing a host would be worse than not rewriting.
 */
export const segmentBaseFor = (path: string, baseURL?: string): string | null => {
    if (!baseURL) {
        return null;
    }
    const lastSlash = path.lastIndexOf('/');
    if (lastSlash < 0) {
        return null;
    }
    return `${baseURL.replace(/\/$/, '')}${path.slice(0, lastSlash)}`;
};

/**
 * Fetches a manifest through the shared axios instance — which is what attaches this member's
 * bearer token, the reason the manifest cannot simply be handed to hls.js as a URL — rewrites it,
 * and returns a `blob:` URL for `hls.loadSource`.
 *
 * The caller owns the returned URL and must `URL.revokeObjectURL` it; nothing here holds a
 * reference. Calling this again is how a player recovers from a segment 403: the signatures in a
 * manifest expire 12 hours after it was minted, and a fresh fetch mints fresh ones.
 *
 * Failures reach the caller. Each player has its own visible error state and its own Slovak-free
 * legacy message; inventing one here would put a second copy of it in a third place.
 */
export const fetchPlaylistObjectUrl = async (path: string): Promise<string> => {
    const response = await api.get(path, { responseType: 'text' });
    const body = String(response.data);
    const base = segmentBaseFor(path, api.defaults.baseURL);
    const playlist = base === null ? body : absolutizeSegmentLines(body, base);
    const blob = new Blob([playlist], { type: 'application/vnd.apple.mpegurl' });
    return URL.createObjectURL(blob);
};
